import { cn } from '@latitude/token-utils';
import { useActionListContext } from 'ActionListContext';
import type { VariantProps } from 'class-variance-authority';
import { Command as CommandPrimitive } from 'cmdk';
import type { ComponentPropsWithoutRef, ComponentRef, Ref } from 'react';
import { commandItemVariants } from 'variants/item-variants';

/**
 * Props for the CommandItem component.
 */
type CommandItemProps = ComponentPropsWithoutRef<typeof CommandPrimitive.Item> &
  VariantProps<typeof commandItemVariants> & {
    isSelected: boolean;
  };

/**
 * Represents a command item component.
 *
 * @component
 * @example
 * ```tsx
 * <CommandItem
 *   className="custom-class"
 *   size="small"
 *   selected={true}
 *   state="active"
 *   onClick={handleClick}
 * />
 * ```
 */
export const CommandItem = ({
  className,
  size,
  isSelected,
  state,
  children,
  ref,
  ...props
}: CommandItemProps & {
  ref?: Ref<ComponentRef<typeof CommandPrimitive.Item>>;
}) => {
  const { showSelectedValue } = useActionListContext();
  return (
    <CommandPrimitive.Item
      {...props}
      ref={ref}
      data-active={isSelected && showSelectedValue}
      className={cn(
        'action-list-item',
        commandItemVariants({
          size,
          state: state === 'selected' && !showSelectedValue ? 'default' : state,
        }),
        className,
      )}
      tabIndex={0}
    >
      {children}
    </CommandPrimitive.Item>
  );
};

CommandItem.displayName = CommandPrimitive.Item.displayName;
